import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { featureFlagsApi } from '@/api/featureFlagsApi'
import { useTenant } from '@/providers/TenantProvider'

/** Prefix key used for all Feature Flag queries (effective map + admin list). */
const FEATURE_FLAGS_KEY = ['featureFlags'] as const

/** Effective flag map (featureKey → enabled) for the currently selected tenant. */
export function useEffectiveFlags() {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: [...FEATURE_FLAGS_KEY, 'effective', selectedTenantId],
    queryFn: () => featureFlagsApi.effective(selectedTenantId ?? undefined),
  })
}

/** Admin list query for one tenant + a mutation to toggle a single flag. */
export function useFeatureFlagsAdmin(tenantId: string | null) {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: [...FEATURE_FLAGS_KEY, 'admin', tenantId],
    queryFn: () => featureFlagsApi.listForTenant(tenantId as string),
    enabled: !!tenantId,
  })

  const setFlag = useMutation({
    mutationFn: (input: { featureKey: string; enabled: boolean }) =>
      featureFlagsApi.setFlag(tenantId as string, input.featureKey, input.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FEATURE_FLAGS_KEY })
    },
  })

  return { list, setFlag }
}
